/**
 * cart-page.js
 * ============
 * Feature 047 (GC-224), the /cart page (snippets/cart.liquid, view 'page'). Loaded by sections/cart.liquid.
 *
 * The phone bar (spec P6, D10): below 990px, .cart-page__bar pins the subtotal and Checkout to the bottom
 * of the screen while the summary card's own Checkout is out of view. It sets html.cart-sticky-shown and
 * --cart-sticky-h, as the site-wide GC-115 bar does, so the chat buttons rise above it and the page pads
 * its end (assets/cart-page.css). Its button clicks the summary's Checkout, or the copy the Route app
 * swaps in, so Route, analytics and CoreCart's GC-208 change guard all apply.
 *
 * A cart change replaces .cart--form, bar and summary included: every lookup goes through the root, and
 * the observer moves to the new summary on theme:cart:updated.
 */
(function () {
  "use strict";

  // Bind once: a theme editor section reload runs this file again.
  if (window.__cartPageBound) {
    return;
  }
  window.__cartPageBound = true;

  var WIDE = window.matchMedia("(min-width: 990px)");
  var observer = null;
  var summaryInView = true;

  /** @returns {?Element} The page's cart, or null on any other page. */
  function root() {
    return document.querySelector('.cart--root[data-view="page"]');
  }

  /**
   * Shows or hides the bar, and tells the rest of the page.
   *
   * @param {boolean} wanted - Whether the summary's Checkout is out of view.
   * @returns {void}
   */
  function show(wanted) {
    var cart = root();
    var bar = cart && cart.querySelector(".cart-page__bar");
    var on = !!bar && wanted && !WIDE.matches;
    var html = document.documentElement;
    var appears = on && !html.classList.contains("cart-sticky-shown");
    if (bar) {
      bar.hidden = !on;
    }
    html.classList.toggle("cart-sticky-shown", on);
    if (on) {
      html.style.setProperty("--cart-sticky-h", bar.offsetHeight + "px");
    }
    if (appears) {
      keepFocusClear(bar);
    }
  }

  /**
   * The scroll that brings a keyboard-focused control to the screen's bottom edge can also take the summary's
   * Checkout out of view, and the bar then appears over that control: move it clear. Keyboard focus only
   * (:focus-visible), so a tap and a scroll are never pulled back.
   *
   * @param {Element} bar - The bar, just shown.
   * @returns {void}
   */
  function keepFocusClear(bar) {
    var focused = document.activeElement;
    var visible = false;
    try {
      visible = !!focused && focused !== document.body && focused.matches(":focus-visible");
    } catch (e) {
      // A browser without :focus-visible: leave the scroll alone.
    }
    if (!visible || bar.contains(focused)) {
      return;
    }
    var under = focused.getBoundingClientRect().bottom - (window.innerHeight - bar.offsetHeight - 16);
    if (under > 0) {
      window.scrollBy(0, under);
    }
  }

  /**
   * Watches the summary's Checkout block the page shows now.
   *
   * @returns {void}
   */
  function watch() {
    var cart = root();
    var target = cart && cart.querySelector(".cart-page__summary .cart-drawer__checkout");
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (!target || !window.IntersectionObserver) {
      show(false);
      return;
    }
    // The redraw brings the bar back hidden: keep it as it was until the observer reports, or it blinks.
    show(!summaryInView);
    observer = new IntersectionObserver(function (entries) {
      summaryInView = entries[entries.length - 1].isIntersecting;
      show(!summaryInView);
    });
    observer.observe(target);
  }

  // The bar's button stands in for the summary's Checkout. Delegated: a redraw replaces the button.
  document.addEventListener("click", function (event) {
    var button = event.target.closest ? event.target.closest(".cart-page__bar-checkout") : null;
    var cart = root();
    if (!button || button.disabled || !cart) {
      return;
    }
    var checkout =
      cart.querySelector(".cart-page__summary button[data-route-copy]") ||
      cart.querySelector('.cart-page__summary button[name="checkout"]');
    if (checkout && !checkout.disabled) {
      checkout.click();
    }
  });

  /**
   * D5: on /cart the header's cart icon takes the shopper to the cart heading, instead of opening the drawer
   * over the same cart. Capture phase on the document, before the theme's own click and Enter handlers on
   * the icon (theme.js OffCanvas), which it stops.
   *
   * @param {Event} event - A click, or a keydown on the icon.
   * @returns {void}
   */
  function toCart(event) {
    var link = event.target.closest ? event.target.closest('a[data-off-canvas--view="cart"]') : null;
    var cart = root();
    if (!link || !cart || (event.type === "keydown" && event.key !== "Enter")) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    var title = cart.querySelector(".cart-page__title");
    if (title) {
      var still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      title.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
      title.focus({ preventScroll: true });
    }
  }
  document.addEventListener("click", toCart, true);
  document.addEventListener("keydown", toCart, true);

  // Back from checkout: the browser can show this page from its back-forward cache, with the cart and
  // Checkout's spinner as they were when the shopper left. Draw every cart again from the cart as it is.
  window.addEventListener("pageshow", function (event) {
    var cart = window.theme && window.theme.partials && window.theme.partials.Cart;
    if (!event.persisted || !root() || !cart) {
      return;
    }
    cart.updateTotals();
    cart.updateAllHtml(function () {});
  });

  if (!root()) {
    return;
  }
  watch();
  window.addEventListener("theme:cart:updated", watch);
  document.addEventListener("shopify:section:load", watch);
  if (WIDE.addEventListener) {
    WIDE.addEventListener("change", function () {
      show(!summaryInView);
    });
  }
})();
